import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import {
    FileText,
    User,
    Calendar,
    Clock,
    Paperclip,
    Play,
    CheckCircle,
    AlertCircle,
    RefreshCw,
    Search,
} from "lucide-react";
import { format } from "date-fns";

const STATUS_CONFIG = {
    pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
    assigned: { label: "Assigned", color: "bg-blue-100 text-blue-800", icon: Clock },
    in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800", icon: RefreshCw },
    completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

export default function ParalegalMyRequests() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    // Fetch paralegal requests
    const { data, isLoading, error } = useQuery({
        queryKey: ["paralegal-requests"],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");
            const res = await api.get("/requests/paralegal-requests", {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data;
        },
    });

    // Update status mutation
    const updateStatusMutation = useMutation({
        mutationFn: async ({ requestId, status }) => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");

            const endpoints = {
                in_progress: `/requests/${requestId}/start-work`,
                completed: `/requests/${requestId}/complete`,
            };

            const endpoint = endpoints[status];
            if (!endpoint) throw new Error("Invalid status transition");

            await api.post(endpoint, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            return { requestId, status };
        },
        onSuccess: (data) => {
            queryClient.setQueryData(["paralegal-requests"], (old) =>
                old.map((req) =>
                    req.id === data.requestId ? { ...req, status: data.status } : req
                )
            );

            const label = data.status === "in_progress" ? "In Progress" : "Completed";

            Swal.fire({
                icon: "success",
                title: "Success!",
                text: `Request is now "${label}".`,
                timer: 2000,
                showConfirmButton: false,
            });
        },
        onError: (err) => {
            Swal.fire({
                icon: "error",
                title: "Update Failed",
                text: err.response?.data?.message || "Could not update status.",
                confirmButtonText: "OK",
            });
        },
    });

    // Filter and search
    const filteredRequests = useMemo(() => {
        if (!data) return [];
        return data.filter((req) => {
            const matchesSearch =
                req.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.form_name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = filterStatus === "all" || req.status === filterStatus;
            return matchesSearch && matchesFilter;
        });
    }, [data, searchTerm, filterStatus]);

    const handleStatusChange = (requestId, currentStatus) => {
        let newStatus = "";
        let title = "";
        let text = "";

        if (currentStatus === "assigned") {
            newStatus = "in_progress";
            title = "Start Working?";
            text = "This will mark the request as 'In Progress'.";
        } else if (currentStatus === "in_progress") {
            newStatus = "completed";
            title = "Complete Request?";
            text = "This will mark the request as 'Completed'.";
        } else {
            return;
        }

        Swal.fire({
            title,
            text,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
        }).then((result) => {
            if (result.isConfirmed) {
                updateStatusMutation.mutate({ requestId, status: newStatus });
            }
        });
    };

    if (isLoading) {
        return (
            <section className="p-6">
                <div className="flex justify-center items-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="ml-3 text-gray-600">Loading your requests...</p>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="p-6">
                <div className="text-center py-12 bg-red-50 rounded-lg">
                    <p className="text-red-600 font-medium">Failed to load requests.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="p-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">My Requests</h2>
                    <p className="text-gray-600 mt-1">Manage and track your assigned legal requests</p>
                </div>
                <div className="text-sm text-gray-500">
                    Total: <span className="font-semibold">{filteredRequests.length}</span> request(s)
                </div>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by title, client, or form..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                    <option value="all">All Status</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                </select>
            </div>

            {/* Requests List */}
            <div className="space-y-4">
                {filteredRequests.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No requests found.</p>
                    </div>
                ) : (
                    filteredRequests.map((request) => {
                        const StatusIcon = STATUS_CONFIG[request.status]?.icon || AlertCircle;
                        const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;

                        return (
                            <motion.div
                                key={request.id}
                                layout
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                                    <div className="flex-1 space-y-3">
                                        {/* Title & Form */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                <FileText className="w-5 h-5 text-blue-600" />
                                                {request.title}
                                            </h3>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Form: <span className="font-medium text-blue-600">{request.form_name}</span>
                                            </p>
                                        </div>

                                        {/* Description */}
                                        <p className="text-gray-700 line-clamp-2">{request.description}</p>

                                        {/* Client & Case */}
                                        <div className="flex flex-wrap gap-4 text-sm">
                                            <div className="flex items-center gap-1">
                                                <User className="w-4 h-4 text-gray-500" />
                                                <span className="text-gray-600">Client:</span>
                                                <span className="font-medium">{request.client_name}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <FileText className="w-4 h-4 text-gray-500" />
                                                <span className="text-gray-600">Case:</span>
                                                <span className="font-medium">{request.case_type}</span>
                                            </div>
                                        </div>

                                        {/* Files & Date */}
                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                            <div className="flex items-center gap-1">
                                                <Paperclip className="w-4 h-4" />
                                                <span>{request.files_count} file(s)</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                <span>
                                                    {format(new Date(request.created_at), "MMM d, yyyy 'at' h:mm a")}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Assigned By */}
                                        <p className="text-xs text-gray-500">
                                            Assigned by: <span className="font-medium">{request.assigned_by}</span>
                                        </p>
                                    </div>

                                    {/* Status & Action */}
                                    <div className="flex items-end gap-3">
                                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                            <StatusIcon className="w-4 h-4" />
                                            <span>{statusConfig.label}</span>
                                        </div>

                                        {request.status === "assigned" && (
                                            <motion.button
                                                onClick={() => handleStatusChange(request.id, request.status)}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                disabled={updateStatusMutation.isPending}
                                            >
                                                <Play className="w-4 h-4" />
                                                Start Work
                                            </motion.button>
                                        )}

                                        {request.status === "in_progress" && (
                                            <motion.button
                                                onClick={() => handleStatusChange(request.id, request.status)}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                disabled={updateStatusMutation.isPending}
                                            >
                                                <CheckCircle className="w-4 h-4" />
                                                Mark Complete
                                            </motion.button>
                                        )}

                                        {request.status === "completed" && (
                                            <div className="text-xs text-green-600 font-medium">Completed</div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })
                )}
            </div>
        </section>
    );
}